import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from 'use-toast-mui';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { getEmployeeIdMembers } from '../../api/staff';
import CustomCheckbox from '../CustomCheckbox';
import { pageSize } from '../../constants/constants';
import { errorMessages } from '../../utils/methods';
import * as XLSX from 'xlsx';
import { CommonExcelButton } from '../../form-components/CommonExcelButton';
import CommonPagination from '../../form-components/CommonPagination';
import CommonSelect from '../../form-components/CommonSelect';
import CoursTicketDetail from '../userDetails/coursTicketDetail/CoursTicketDetail';

// ============================================
// Styled Components
// ============================================
const Container = styled(Box)({
  backgroundColor: '#fff',
  borderRadius: '20px',
  boxShadow: '0 1px 8px rgba(0,0,0,0.06)',
  padding: '28px 32px',
});

const HeaderRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '16px',
});

const Title = styled(Typography)({
  fontSize: '17px',
  fontWeight: 700,
  color: '#262626',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  '&::before': {
    content: '""',
    width: '4px',
    height: '18px',
    borderRadius: '2px',
    backgroundColor: '#4ab3bc',
    display: 'inline-block',
  },
});

const CountBadge = styled('span')({
  fontSize: '14px',
  fontWeight: 600,
  color: '#4ab3bc',
  marginLeft: '4px',
});

const HeaderCell = styled(TableCell)({
  fontWeight: 600,
  fontSize: '13px',
  color: '#737373',
  padding: '10px 12px',
  borderRight: '1px solid #f2f2f2',
  borderBottom: 'none',
  textAlign: 'center',
  whiteSpace: 'nowrap',
  '&:last-child': { borderRight: 'none' },
});

const BodyRow = styled(TableRow)({
  height: '44px',
  transition: 'background 0.1s',
  '&:hover': { backgroundColor: '#f8fafb' },
});

const BodyCell = styled(TableCell)({
  fontSize: '13px',
  fontWeight: 500,
  color: '#404040',
  padding: '8px 12px',
  borderRight: '1px solid #f5f5f5',
  textAlign: 'center',
  whiteSpace: 'nowrap',
  '&:last-child': { borderRight: 'none' },
});

const NameLink = styled('span')({
  cursor: 'pointer',
  fontWeight: 600,
  color: '#262626',
  '&:hover': { color: '#4ab3bc', textDecoration: 'underline' },
});

const TicketBadge = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 8px',
  borderRadius: '6px',
  backgroundColor: '#f0fafb',
  color: '#3a9da5',
  fontSize: '12px',
  fontWeight: 600,
  margin: '1px 2px',
  whiteSpace: 'nowrap',
});

const InfoButton = styled(Button)({
  backgroundColor: '#4ab3bc',
  color: '#fff',
  borderRadius: '8px',
  textTransform: 'none',
  fontSize: '12px',
  fontWeight: 600,
  padding: '4px 14px',
  minWidth: '80px',
  boxShadow: 'none',
  '&:hover': { backgroundColor: '#3a9da5', boxShadow: 'none' },
});

const ConsentChip = styled(Typography, {
  shouldForwardProp: prop => prop !== 'agreed',
})(({ agreed }) => ({
  display: 'inline-block',
  fontSize: '12px',
  fontWeight: 600,
  padding: '2px 10px',
  borderRadius: '6px',
  color: agreed ? '#4ab3bc' : '#a3a3a3',
  backgroundColor: agreed ? '#f0fafb' : '#f5f5f5',
}));

const initialQuery = { page: 1, limit: 10 };

export default function EmployeeTab2() {
  const bigUser = useSelector(state => state.user.user);
  const token = bigUser.token;
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState(initialQuery);
  const [ticketModalMember, setTicketModalMember] = useState(null);

  const handleChangePage = (_, value) => setQuery({ ...query, page: value });

  const handleChange = e => {
    const { name, checked } = e.target;
    if (name === 'allSelect') {
      setUsers({ ...users, content: users?.content?.map(user => ({ ...user, isChecked: checked })) });
    } else {
      setUsers({
        ...users,
        content: users?.content?.map((user, index) =>
          index + user.memberId === name ? { ...user, isChecked: checked } : user,
        ),
      });
    }
  };

  const employeeMembers = async () => {
    try {
      setLoading(true);
      const res = await getEmployeeIdMembers(token, id, query);
      setUsers(res);
    } catch (error) {
      toast.error(errorMessages(error));
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    const filteredData = users?.content
      ?.filter(user => user.isChecked)
      ?.map(({ username, phoneNumber, firstPaymentDate, smsreceive, age, activeTickets }) => ({
        이름: username,
        전화번호: phoneNumber,
        '첫 결제일': firstPaymentDate,
        '수신 동의': smsreceive ? 'YES' : 'NO',
        나이: age,
        '사용 수강권': activeTickets.map(ticket => ticket.ticketName).join(', '),
      }));
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(filteredData);
    XLSX.utils.book_append_sheet(wb, ws, 'Users');
    XLSX.writeFile(wb, 'data.xlsx');
  };

  useEffect(() => {
    employeeMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const checkedCount = users?.content?.filter(u => u.isChecked)?.length || 0;

  return (
    <Container>
      <HeaderRow>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Title>
            담당 회원 내역
            <CountBadge>{users?.totalElements || 0}명</CountBadge>
          </Title>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <CommonExcelButton onClick={exportToExcel} disabled={checkedCount === 0}>
            {checkedCount > 0 ? `선택 엑셀 다운로드 (${checkedCount})` : '엑셀 다운로드'}
          </CommonExcelButton>
          <CommonSelect
            value={query.limit}
            onChange={e => setQuery({ ...query, limit: e.target.value, page: 1 })}
            width={108}
            options={pageSize}
          />
        </Box>
      </HeaderRow>

      <TableContainer sx={{ border: '1px solid #f2f2f2', borderRadius: '12px', overflow: 'hidden' }}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: '#f4f6f9' }}>
            <TableRow sx={{ height: '40px' }}>
              <HeaderCell sx={{ width: 48 }}>
                <CustomCheckbox
                  color="primary"
                  name="allSelect"
                  checked={users?.content?.length > 0 && !users?.content?.some(user => user?.isChecked !== true)}
                  onChange={handleChange}
                />
              </HeaderCell>
              <HeaderCell>{t('Name')}</HeaderCell>
              <HeaderCell>{t('Gender')}</HeaderCell>
              <HeaderCell>{t('Age')}</HeaderCell>
              <HeaderCell>{t('Phone Number')}</HeaderCell>
              <HeaderCell>이용중 수강권</HeaderCell>
              <HeaderCell>{t('First Payment Date')}</HeaderCell>
              <HeaderCell>{t('Recent Visit')}</HeaderCell>
              <HeaderCell>락커 만료일</HeaderCell>
              <HeaderCell>수신동의</HeaderCell>
              <HeaderCell>회원권</HeaderCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              Array.from({ length: query.limit }).map((_, i) => (
                <BodyRow key={`sk-${i}`}>
                  {Array.from({ length: 11 }).map((__, j) => (
                    <BodyCell key={j}><Skeleton variant="text" width={j === 0 ? 18 : 60} sx={{ mx: 'auto' }} /></BodyCell>
                  ))}
                </BodyRow>
              ))
            ) : users?.content?.length > 0 ? (
              users.content.map((row, index) => (
                <BodyRow key={index}>
                  <BodyCell>
                    <CustomCheckbox
                      name={index + row?.memberId}
                      checked={row?.isChecked || false}
                      onChange={handleChange}
                    />
                  </BodyCell>
                  <BodyCell>
                    <NameLink onClick={() => navigate(`/member/${row.memberId}`)}>
                      {row.name}
                    </NameLink>
                  </BodyCell>
                  <BodyCell>{row.gender === 'FEMALE' ? '여성' : '남성'}</BodyCell>
                  <BodyCell>{row.age || '-'}</BodyCell>
                  <BodyCell>{row.phoneNumber}</BodyCell>
                  <BodyCell sx={{ whiteSpace: 'normal', maxWidth: '240px' }}>
                    {row?.activeTickets?.length > 0 ? (
                      row.activeTickets.map((item, i) => (
                        <TicketBadge key={i}>
                          {item.ticketName} {item.remainingSessionCount}/{item.totalSessionCount}
                        </TicketBadge>
                      ))
                    ) : '-'}
                  </BodyCell>
                  <BodyCell>{row?.firstPaymentDate || '-'}</BodyCell>
                  <BodyCell>{row?.lastAttendanceDate || '-'}</BodyCell>
                  <BodyCell>{row?.lockerExpireDate || '-'}</BodyCell>
                  <BodyCell>
                    <ConsentChip agreed={row?.smsreceive}>
                      {row?.smsreceive ? '동의' : '비동의'}
                    </ConsentChip>
                  </BodyCell>
                  <BodyCell>
                    <InfoButton variant="contained" disableElevation onClick={() => setTicketModalMember(row)}>
                      상세보기
                    </InfoButton>
                  </BodyCell>
                </BodyRow>
              ))
            ) : (
              <TableRow>
                <BodyCell colSpan={11} sx={{ py: '60px', color: '#a3a3a3', fontSize: '14px' }}>
                  담당 회원이 없습니다.
                </BodyCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <CommonPagination
        totalElements={users?.totalElements}
        limit={query.limit}
        page={query.page}
        onChange={handleChangePage}
      />

      <Dialog
        open={Boolean(ticketModalMember)}
        onClose={() => setTicketModalMember(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          fontSize: '16px', fontWeight: 700, borderBottom: '1px solid #f2f2f2', py: 1.5,
        }}>
          {ticketModalMember?.name ? `${ticketModalMember.name} 회원 회원권 정보` : '회원권 정보'}
          <IconButton size="small" onClick={() => setTicketModalMember(null)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {ticketModalMember && (
            <CoursTicketDetail user={ticketModalMember} bigUser={bigUser} onTicketsUpdate={employeeMembers} />
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
}
